import React, { useMemo } from 'react';
import { useRouter } from 'next/navigation';

import { Table } from 'components/MRT';
import { useRouteUtils } from 'routes';
import { colors } from 'core/styles';

import { createColumns } from './tableColumns';
import { Context, Pagination, TableData } from './types';

type PositionsTableProps = {
  positions: TableData[];
  total: number;
  context: Context;
  pagination: Pagination;
  loading: boolean | undefined;
  setPagination: (params: Pagination) => void;
  hideAmountColumns: boolean;
};

export function PositionsTableView({
  positions,
  total,
  pagination: { limit, offset },
  context,
  loading,
  setPagination,
  hideAmountColumns,
}: PositionsTableProps) {
  const { redirectTo } = useRouteUtils();
  const displaysFiltersAndPagination = total / limit > 1;
  const router = useRouter();

  const paginationState = useMemo(
    () => ({
      pageIndex: Math.round(offset / limit),
      pageSize: limit,
    }),
    [limit, offset],
  );

  const columns = useMemo(
    () => createColumns(hideAmountColumns, context),
    [context, hideAmountColumns],
  );

  return (
    <Table<TableData>
      initialState={{
        density: 'compact',
        columnOrder: hideAmountColumns
          ? ['name', 'lastActive']
          : ['name', 'riskFactor', 'deposits', 'collaterals', 'borrows', 'lastActive', 'assets'],
        displaySettings: {
          fallbackedTotalUSDVariant: 'value',
        },
      }}
      state={{ pagination: paginationState, showProgressBars: loading }}
      manualPagination
      rowCount={total}
      onPaginationChange={stateFn => {
        const state = typeof stateFn === 'function' ? stateFn(paginationState) : stateFn;

        setPagination({
          limit: state.pageSize,
          offset: state.pageSize * state.pageIndex,
        });
      }}
      autoPinningColumns={['name']}
      data={positions}
      columns={columns}
      enablePagination={displaysFiltersAndPagination}
      enableColumnFilterModes
      enablePinning={false}
      enableToolbarInternalActions={false}
      enableTopToolbar={false}
      withFullScreenButton={!hideAmountColumns}
      displayColumnDefOptions={{
        'mrt-row-actions': {
          minSize: 100,
          size: 100,
          muiTableHeadCellProps: {
            align: 'center',
            sx: { flexGrow: 0 },
          },
          muiTableBodyCellProps: {
            align: 'center',
            sx: { flexGrow: 0 },
          },
        },
      }}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => {
          const path = redirectTo.position({
            productSlug: row.original.productSlug,
            versionSlug: row.original.versionSlug,
            network: row.original.network,
            accountId: row.original.account,
            marketId: row.original.marketId,
          }).href;

          router.push(path);
        },
        sx: {
          '&:hover': {
            cursor: 'pointer',

            '& .MuiTableCell-root': {
              background: colors.silver,
            },
          },
          cursor: 'pointer',
        },
      })}
    />
  );
}
